import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useParams,
  Navigate,
} from 'react-router-dom';
import { ADMIN_PANEL_BASE_PATH, isAdminPanelPath } from './constants/adminPanelRoutes';
import './styles/global.css';
import './styles/darkThemeStorefront.css';
import './App.css';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import MiniFooter from './components/MiniFooter';
import { ToastManager } from './components/Toast';
import CheckoutSuccess from './components/CheckoutSuccess';
import PromoPopup from './components/PromoPopup';
import FloatingWhatsAppButton from './components/FloatingWhatsAppButton';
// Pages
import Home from './pages/Home';
import CategoryPage from './pages/CategoryPage';
import AllCategoriesPage from './pages/AllCategoriesPage';
import ProductPage from './pages/ProductPage';
import StorefrontSingleSegmentPage from './pages/StorefrontSingleSegmentPage';
import BagPage from './pages/BagPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminPanel from './admin/AdminPanel';
import MaintenancePage from './pages/MaintenancePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ReturnPolicyPage from './pages/ReturnPolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import {
  readSiteSettingsFromStorage,
  syncPublicSiteSettings,
  notifySiteBrandingUpdated,
} from './utils/siteBranding';
import { BagProvider } from './context/BagContext';
import { AuthProvider } from './context/AuthContext';
import { PaymentProvider } from './context/PaymentContext';
import { OrderProvider } from './context/OrderContext';
import { LikedItemsProvider } from './context/WishlistContext';
import { TrackingProvider } from './context/TrackingContext';
import { AddressProvider } from './context/AddressContext';
import { ProductsProvider } from './context/ProductsContext';
import { PushNotificationProvider } from './context/PushNotificationContext';
import { ThemeProvider } from './context/ThemeContext';

/** Storefront routes where the main site footer is shown */
const FOOTER_PATHS = new Set(['/', '', '/about', '/contact', '/return-policy', '/privacy-policy', '/payment-methods']);

/** Legacy `/creator/:id` and `/creators/:id` → `/:id` (root shop URL). */
function LegacyCreatorRedirect() {
  const { creatorId, creatorSlugOrId } = useParams();
  const raw = creatorId || creatorSlugOrId || '';
  if (!raw) return <Navigate to="/" replace />;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }
  return <Navigate to={`/${encodeURIComponent(decoded)}`} replace />;
}

// ScrollToTop component to handle automatic scrolling to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Check if maintenance mode is enabled (from site settings)
const isMaintenanceMode = () => {
  try {
    const settings = readSiteSettingsFromStorage();
    if (settings) return !!settings.maintenanceMode;
  } catch (e) {}
  return false;
};

// Main App component with conditional navigation
function AppContent() {
  const location = useLocation();
  const isAdminRoute = isAdminPanelPath(location.pathname);
  const showFullFooter = !isAdminRoute && FOOTER_PATHS.has(location.pathname);
  const isAuthScreenRoute = location.pathname === '/login' || location.pathname === '/signup';
  const showMiniFooter = !isAdminRoute && !showFullFooter && !isAuthScreenRoute;
  const [maintenanceOn, setMaintenanceOn] = useState(() => isMaintenanceMode());

  useEffect(() => {
    setMaintenanceOn(isMaintenanceMode());
  }, [location.pathname]);

  useEffect(() => {
    const check = () => setMaintenanceOn(isMaintenanceMode());
    window.addEventListener('maintenanceModeChanged', check);
    return () => window.removeEventListener('maintenanceModeChanged', check);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await syncPublicSiteSettings();
      if (!cancelled) {
        notifySiteBrandingUpdated();
        setMaintenanceOn(isMaintenanceMode());
        window.dispatchEvent(new CustomEvent('maintenanceModeChanged'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-apply favicon/title when switching routes (e.g. /sw92team → shop) so tab icon matches stored logo.
  useEffect(() => {
    notifySiteBrandingUpdated();
  }, [location.pathname]);
  
  const [checkoutSuccess, setCheckoutSuccess] = useState({
    isVisible: false,
    orderDetails: {}
  });

  useEffect(() => {
    // Listen for checkout success events
    const handleCheckoutSuccess = (event) => {
      setCheckoutSuccess({
        isVisible: true,
        orderDetails: event.detail || {}
      });
    };

    window.addEventListener('showCheckoutSuccess', handleCheckoutSuccess);
    
    return () => {
      window.removeEventListener('showCheckoutSuccess', handleCheckoutSuccess);
    };
  }, []);

  const handleCloseCheckoutSuccess = () => {
    setCheckoutSuccess({
      isVisible: false,
      orderDetails: {}
    });
  };

  if (maintenanceOn && !isAdminRoute) {
    return <MaintenancePage />;
  }

  return (
    <div className="App">
      {!isAdminRoute && <Header />}
      <main
        className={
          isAdminRoute
            ? 'admin-content'
            : `main-content${isAuthScreenRoute ? ' main-content--auth-screen' : ''}`
        }
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/categories" element={<AllCategoriesPage />} />
          <Route path="/category/:category" element={<CategoryPage />} />
          <Route path="/category/:category/:subcategory" element={<CategoryPage />} />
          <Route path="/category/:category/:subcategory/:tag" element={<CategoryPage />} />
          <Route path="/tag/:tag" element={<CategoryPage />} />
          <Route path="/brand/:brand" element={<CategoryPage />} />
          <Route path="/search" element={<CategoryPage />} />
          <Route path="/creator/:creatorId" element={<LegacyCreatorRedirect />} />
          <Route path="/creators/:creatorSlugOrId" element={<LegacyCreatorRedirect />} />
          {/* Legacy product route (for backward compatibility) */}
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/bag" element={<BagPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order/:orderId/success" element={<OrderSuccessPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:resetToken" element={<ResetPasswordPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/return-policy" element={<ReturnPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/payment-methods" element={<PaymentMethodsPage />} />
          {/* Legacy `/admin` URL disabled — send to storefront (avoid slug route treating "admin" as product). */}
          <Route path="/admin/*" element={<Navigate to="/" replace />} />
          <Route path={ADMIN_PANEL_BASE_PATH} element={<AdminPanel />} />
          <Route path={`${ADMIN_PANEL_BASE_PATH}/*`} element={<AdminPanel />} />
          {/* SEO-friendly product slug — must be last so /bag, /sw92team, /login, etc. are not captured */}
          <Route path="/:productUrl" element={<StorefrontSingleSegmentPage />} />
        </Routes>
      </main>
      {showFullFooter && <Footer />}
      {showMiniFooter && <MiniFooter />}
      <ToastManager />
      {!isAdminRoute && (
        <>
          <FloatingWhatsAppButton />
          <PromoPopup />
          <CheckoutSuccess
            isVisible={checkoutSuccess.isVisible}
            onClose={handleCloseCheckoutSuccess}
            orderDetails={checkoutSuccess.orderDetails}
          />
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <Router basename={process.env.PUBLIC_URL || ''}>
      <ThemeProvider>
      <PushNotificationProvider>
        <AuthProvider>
          <AddressProvider>
            <PaymentProvider>
              <OrderProvider>
                <BagProvider>
                  <LikedItemsProvider>
                    <TrackingProvider>
                      <ProductsProvider>
                        <ScrollToTop />
                        <AppContent />
                      </ProductsProvider>
                    </TrackingProvider>
                  </LikedItemsProvider>
                </BagProvider>
              </OrderProvider>
            </PaymentProvider>
          </AddressProvider>
        </AuthProvider>
      </PushNotificationProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
